import {
  AuthEndpoint,
  useConnectionStatus,
  useYDoc,
  YDocProvider,
} from "@y-sweet/react";
import React, { useState } from "react";
import "./App.css";

import { useAtom } from "jotai";
import { editorDeniedAtom, fontSizeAtom, isEditorAtom, languages } from "./configAtoms";
import { FontSizeControls } from "./FontSizeControls";
import { useStrings, resolveLocale, LANGUAGE_BCP47 } from "./useLocale";
import {
  LISTEN_LANGUAGE_CODES,
  listenFavorites,
  defaultListenCode,
} from "./listenLanguages";
import { useSourceLanguage } from "./useSourceLanguage";
import { LandingPageContainer } from "./LandingPage";
import type { ClientToken } from "@y-sweet/sdk";
import { SourceTextTranslationManager } from "./SourceTextTranslationManager";
import { PostHogErrorBoundary } from "posthog-js/react";
import { CurrentSlideViewerContainer } from "./CurrentSlideViewer";
import { BilingualBlockViewerContainer } from "./BilingualBlockViewerContainer";
import { SlideReviewContainer } from "./SlideReviewContainer";
import { SlideTranslationViewerContainer } from "./SlideTranslationViewer";
import { StatusViewContainer } from "./StatusView";
import { getDocId } from "./getDocId";
import { SessionGate } from "./SessionGate";
import { apiFetch, promptForWriteKeyOnce } from "./writeKey";
import { useAutoHideChrome } from "./reactUtils";

// Lazy-loaded so the heavy LiveKit client SDK is only fetched when a live-audio
// pane (listen-{language} / broadcast) is actually rendered, keeping it out of
// the main bundle and isolating the feature.
const ListenViewer = React.lazy(() =>
  import("./ListenViewer").then((m) => ({ default: m.ListenViewer }))
);
const BroadcastControl = React.lazy(() =>
  import("./BroadcastControl").then((m) => ({ default: m.BroadcastControl }))
);

function ConnectionStatusWidget({
  connectionStatus,
}: {
  connectionStatus: string;
}) {
  const s = useStrings();
  if (connectionStatus === "connected") {
    return null; // Don't show anything if connected
  }
  return (
    <div
      className={`px-1 py-1 rounded-full text-xs font-medium ${
        connectionStatus === "connecting"
          ? "bg-yellow-500 text-white"
          : "bg-red-500 text-white"
      }`}
    >
      {connectionStatus === "connecting" ? s.connecting : s.disconnected}
    </div>
  );
}

function PagePart({ componentStr, onReplace }: { componentStr: string; onReplace: (newName: string) => void }) {
  const [fontSize] = useAtom(fontSizeAtom);
  const ydoc = useYDoc();
  // eslint-disable-next-line react-hooks/immutability, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  (window as any).ydoc = ydoc; // Expose YDoc on window for debugging
  const s = useStrings();
  const locale = resolveLocale();
  // What the speaker is speaking. The listen picker's "Original" entry is this
  // language, not a constant, so a session spoken in Spanish offers "Original /
  // Spanish" and lists English among the translations like any other target.
  const sourceLanguage = useSourceLanguage();

  const onLanguageChange = (prefix: string) => (newLang: string) => {
    onReplace(`${prefix}-${newLang}`);
  };

  const langDisplayNames = new Intl.DisplayNames([locale], { type: 'language' });

  const languageSelector = (prefix: string, language: string) => (
    <select
      className="ml-2 px-1 py-0.5 rounded text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700"
      value={language}
      onChange={(e) => onLanguageChange(prefix)(e.target.value)}
    >
      {languages.map((lang) => (
        <option key={lang} value={lang}>{langDisplayNames.of(LANGUAGE_BCP47[lang]) ?? lang}</option>
      ))}
    </select>
  );

  // The listen picker offers the full Gemini-supported language set (keyed by
  // BCP-47 code), with "Original" and favorites pinned on top. Names are localized
  // via Intl.DisplayNames; the long list is sorted by localized name. The spoken
  // language is filtered out of both groups — it is the "Original" entry.
  const favoriteListenLangs = listenFavorites(sourceLanguage);
  const sortedListenLangs = LISTEN_LANGUAGE_CODES
    .filter((c) => c !== sourceLanguage && !favoriteListenLangs.includes(c))
    .sort((a, b) =>
      (langDisplayNames.of(a) ?? a).localeCompare(langDisplayNames.of(b) ?? b, locale)
    );

  const listenLanguageSelector = (language: string) => (
    <select
      className="ml-2 px-1 py-0.5 rounded text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700"
      value={language}
      onChange={(e) => onLanguageChange('listen')(e.target.value)}
    >
      <option value={sourceLanguage}>
        {`${s.listenOriginal} / ${langDisplayNames.of(sourceLanguage) ?? sourceLanguage}`}
      </option>
      <optgroup label={s.favorites}>
        {favoriteListenLangs.map((c) => (
          <option key={c} value={c}>{langDisplayNames.of(c) ?? c}</option>
        ))}
      </optgroup>
      <optgroup label={s.allLanguages}>
        {sortedListenLangs.map((c) => (
          <option key={c} value={c}>{langDisplayNames.of(c) ?? c}</option>
        ))}
      </optgroup>
    </select>
  );

  const cardClass = "rounded-md shadow bg-gray-100/80 dark:bg-gray-800/80 p-2 mb-2 flex flex-col gap-1 transition hover:shadow-lg";

  if (componentStr === 'sourceText') {
    return (
      <div className={cardClass + " flex-1/2 overflow-auto bg-white/70 dark:bg-gray-900/70"}>
        <SourceTextTranslationManager ydoc={ydoc} />
      </div>
    );
  }

  if (componentStr === 'currentSlide') {
    return <CurrentSlideViewerContainer />;
  }

  if (componentStr === 'status') {
    return <StatusViewContainer />;
  }

  if (componentStr === 'slideReview') {
    return (
      <div className={cardClass + " flex-1 overflow-auto bg-white/70 dark:bg-gray-900/70"}>
        <SlideReviewContainer />
      </div>
    );
  }

  if (componentStr.startsWith('slideTranslation-')) {
    const language = componentStr.substring('slideTranslation-'.length);
    const validLanguage = (languages as readonly string[]).includes(language) ? language : languages[0];
    return (
      <div className={cardClass + " flex-1/2 overflow-auto"}>
        <div className="flex items-center gap-2 px-1">
          <h2 className="font-semibold text-xs text-gray-500 dark:text-gray-300 leading-tight mb-0">{s.translation}</h2>
          {languageSelector('slideTranslation', validLanguage)}
        </div>
        <SlideTranslationViewerContainer language={validLanguage} />
      </div>
    );
  }

  if (componentStr.startsWith('translatedText-')) {
    const language = componentStr.substring('translatedText-'.length);
    const validLanguage = (languages as readonly string[]).includes(language) ? language : languages[0];
    return (
      <div className={cardClass + " flex-1/2 bg-gray-100/80 dark:bg-gray-900/60 text-gray-900 dark:text-gray-100 overflow-auto"}>
        <BilingualBlockViewerContainer
          language={validLanguage}
          fontSize={fontSize}
          showOriginal={false}
          headerControls={
            <>
              <h2 className="font-semibold text-xs text-gray-500 dark:text-gray-300 leading-tight mb-0">{s.translation}</h2>
              {languageSelector('translatedText', validLanguage)}
              <FontSizeControls />
            </>
          }
        />
      </div>
    );
  }

  if (componentStr.startsWith('listen-')) {
    const language = componentStr.substring('listen-'.length);
    const validLanguage =
      language === sourceLanguage || LISTEN_LANGUAGE_CODES.includes(language)
        ? language
        : defaultListenCode(sourceLanguage);
    return (
      <div className={cardClass + " flex-1/2 bg-gray-100/80 dark:bg-gray-900/60 text-gray-900 dark:text-gray-100 overflow-hidden"}>
        <div className="flex items-center">
          <h2 className="font-semibold text-xs text-gray-500 dark:text-gray-300 leading-tight mb-0">{s.listenLive}</h2>
          {listenLanguageSelector(validLanguage)}
          <FontSizeControls />
        </div>
        <React.Suspense fallback={<div className="flex-1 flex items-center justify-center text-xs text-gray-400">{s.connecting}</div>}>
          <ListenViewer key={validLanguage} language={validLanguage} />
        </React.Suspense>
      </div>
    );
  }

  if (componentStr === 'broadcast') {
    return (
      <div className={cardClass + " flex-1/2 bg-gray-100/80 dark:bg-gray-900/60 text-gray-900 dark:text-gray-100 overflow-hidden"}>
        <h2 className="font-semibold text-xs text-gray-500 dark:text-gray-300 leading-tight mb-0">{s.broadcast}</h2>
        <React.Suspense fallback={<div className="flex-1 flex items-center justify-center text-xs text-gray-400">{s.connecting}</div>}>
          <BroadcastControl />
        </React.Suspense>
      </div>
    );
  }

  if (componentStr.startsWith('bilingual-')) {
    const language = componentStr.substring('bilingual-'.length);
    const validLanguage = (languages as readonly string[]).includes(language) ? language : languages[0];
    return (
      <div className={cardClass + " flex-1/2 bg-gray-100/80 dark:bg-gray-900/60 text-gray-900 dark:text-gray-100 overflow-auto"}>
        <BilingualBlockViewerContainer
          language={validLanguage}
          fontSize={fontSize}
          headerControls={
            <>
              <h2 className="font-semibold text-xs text-gray-500 dark:text-gray-300 leading-tight mb-0">{s.bilingual}</h2>
              {languageSelector('bilingual', validLanguage)}
              <FontSizeControls />
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className={cardClass + " flex-1/2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 items-center justify-center"}>
      <p className="text-sm">Unknown component: <code>{componentStr}</code></p>
      <a href="/" className="text-xs underline hover:text-red-900 dark:hover:text-red-100">{s.goHome}</a>
    </div>
  );
}

// Layout page: render the selected layout from URL
/**
 * The layout URL grammar: `|` separates columns, `,` stacks panes within a column.
 * "translatedText-French,currentSlide|listen-fr" => [["translatedText-French", "currentSlide"], ["listen-fr"]]
 *
 * Columns sit side by side on a wide screen and stack on a narrow one. This is the
 * definition; docs/LAYOUT_URLS.md has worked examples for people building a URL by hand.
 */
function parseLayoutString(layoutStr: string | undefined): string[][] {
  if (!layoutStr) return [];
  return layoutStr.split("|").map(col => col.split(","));
}

function formatLayoutString(columns: string[][]): string {
  return columns.map(col => col.join(",")).join("|");
}

function LayoutPage({ layout: initialLayout }: { layout: string }) {
  const connectionStatus = useConnectionStatus();
  const s = useStrings();

  // Track current layout in state so we can update it when URL changes
  const [layout, setLayout] = useState(initialLayout);

  function replaceComponent(colIdx: number, paneIdx: number, newName: string) {
    const parsedLayout = parseLayoutString(layout);
    const newLayout = parsedLayout.map((col, c) =>
      col.map((component, p) => (c === colIdx && p === paneIdx) ? newName : component)
    );
    const newLayoutStr = formatLayoutString(newLayout);
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;
    window.history.replaceState(null, '', `/${newLayoutStr}${currentSearch}${currentHash}`);
    setLayout(newLayoutStr);
  }

  const parsedLayout = parseLayoutString(layout);

  // Render layout columns
  const columns = parsedLayout.map((col, i) => {
    if (col.length === 0) return null;
    return (
      <div
        // biome-ignore lint/suspicious/noArrayIndexKey: The array index is stable
        key={i}
        className={
          parsedLayout.length === 1
            ? "w-full h-full flex flex-col gap-2 p-2"
            : "flex flex-col w-full md:w-1/2 h-1/2 md:h-full gap-2 p-1"
        }
      >
        {col.map((componentStr, j) =>
          <PostHogErrorBoundary key={`${componentStr}-${i}-${j}`} fallback={<div>Error loading component {componentStr}</div>}>
            <PagePart
              componentStr={componentStr}
              onReplace={(newName) => replaceComponent(i, j, newName)}
            />
          </PostHogErrorBoundary>
        )}
      </div>
    );
  });

  const chromeVisible = useAutoHideChrome();
  // pointer-events-none while faded so the invisible controls don't swallow
  // taps meant for whatever's underneath (transcript text, slide content, ...).
  const chromeClass = `transition-opacity duration-300 ${chromeVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`;

  // h-screen is the fallback for browsers without dvh (older Android Chrome). Without a
  // height, the panes never get a fixed height to scroll inside, and touch-none here stops
  // the page itself from scrolling, so the content is stuck.
  return (
    <div className="flex flex-col md:flex-row h-screen supports-[height:100dvh]:h-dvh overflow-hidden relative touch-none">
      <div className={`absolute top-2 right-2 z-10 flex items-center space-x-2 ${chromeClass}`}>
        <ConnectionStatusWidget connectionStatus={connectionStatus} />
      </div>
      <a
        href="/"
        className={`fixed bottom-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-gray-500/70 dark:bg-gray-700/80 text-white shadow-md hover:bg-gray-700/80 dark:hover:bg-gray-600/80 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-600 ${chromeClass}`}
        title={s.goHome}
        style={{ fontSize: '1.3rem', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}
      >
        <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>🏠</span>
      </a>
      {columns}
    </div>
  );
}

/**
 * Shown when a device asked for `#editor` but the server would only issue a read-only
 * token — i.e. this device has no valid write key. Fixed rather than in flow so it can't
 * reshuffle a projected layout, and dismissible because the session still works fine
 * read-only and nobody needs a banner over the words during a service.
 */
function EditorDeniedBanner() {
  const s = useStrings();
  const [denied] = useAtom(editorDeniedAtom);
  const [dismissed, setDismissed] = useState(false);
  if (!denied || dismissed) return null;
  return (
    <div className="fixed top-2 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2 rounded-lg shadow-lg bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100 text-sm max-w-[90vw]">
      <span>{s.editorAccessDenied}</span>
      <button
        type="button"
        className="text-xs underline whitespace-nowrap"
        onClick={() => setDismissed(true)}
      >
        {s.dismiss}
      </button>
    </div>
  );
}

/**
 * Everything below the session gate. Split out so `getDocId()` — and every pane that
 * calls it — runs only after the server has told us which session we are in (#111).
 */
const SessionApp = () => {
  // We're an editor only if location hash includes #editor
  const isEditor = window.location.hash.includes("editor");

  // The server's answer, or the ?doc= override. Shared with the live-audio panes via
  // getDocId() so the LiveKit room matches the session.
  const docId = getDocId();

  const [, setIsEditor] = useAtom(isEditorAtom);
  const [, setEditorDenied] = useAtom(editorDeniedAtom);
  const s = useStrings();
  React.useEffect(() => {
    setIsEditor(isEditor);
  }, [isEditor, setIsEditor]);
  const authEndpoint: AuthEndpoint = async () => {
    const requestToken = async () => {
      const response = await apiFetch("/api/ys-auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ docId, isEditor }),
      });
      return {
        // The server decides what we actually get: asking to edit without a valid write
        // key is answered with a read-only token rather than an error, so the session
        // still displays.
        granted: response.headers.get("X-Granted-Authorization"),
        // Why the key was or wasn't accepted — absent for a viewer request, which is
        // never evaluated.
        keyStatus: response.headers.get("X-Write-Key-Status"),
        clientToken: (await response.json()) as ClientToken,
      };
    };

    let result = await requestToken();
    // Provisioning: this device asked to edit and the server didn't recognize its key,
    // so ask for one and try again. Keyed on the key's status rather than on what was
    // granted, so it also fires in observe mode — where nothing is refused, and which is
    // precisely the window in which devices are supposed to be getting their keys.
    if (isEditor && result.keyStatus && result.keyStatus !== "ok") {
      if (promptForWriteKeyOnce(s.enterWriteKey)) result = await requestToken();
    }
    // Record what we ended up with, so every isEditor-gated control degrades to the
    // viewer experience rather than offering edits that silently do nothing.
    if (isEditor && result.granted === "read-only") {
      console.warn(
        "[write-auth] editor access was not granted for this device; continuing read-only",
      );
      setIsEditor(false);
      setEditorDenied(true);
    }
    return result.clientToken;
  };

  // Parse URL path to determine which page to render
  let pageComponent: React.ReactElement;
  const pathname = decodeURIComponent(window.location.pathname);
  if (pathname === "/" || pathname === "") {
    pageComponent = <LandingPageContainer />;
  } else {
    // Remove leading slash and use as layout string
    // Note: this will be validated inside LayoutPage
    const layout = pathname.substring(1);
    // Apply backwards compat fixes: we used to call translatedText "translatedOutline"
    const fixedLayout = layout.replace(/translatedOutline/g, "translatedText");
    pageComponent = <LayoutPage layout={fixedLayout} />;
  }

  return (
    <YDocProvider docId={docId} authEndpoint={authEndpoint}>
      <EditorDeniedBanner />
      {pageComponent}
    </YDocProvider>
  );
};

const App = () => (
  <SessionGate>
    <SessionApp />
  </SessionGate>
);

export default App;
